import { createContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  IN_CALL_SEARCH_EVENT,
  inCallSearchReceiverFor,
  registerInCallSearchReceiver,
  subscribeInCallSearchReceivers,
  takePendingInCallSearch,
  type InCallSearchRequest,
} from './inCallSearch';

export interface InCallSearchLayout {
  setMainTab: (tab: 'search') => void;
  rightPanelCollapsed: boolean;
  toggleRightPanel: () => void;
  searchEnabled: boolean;
}

/**
 * The joined call's side of the header search (lib/search/inCallSearch).
 * Registers the call as the receiver for `callId` and returns the layout's
 * token for InCallSearchScope; on each request it selects
 * the Search Databases tab and opens a collapsed right panel, where the panel
 * runs the held query. With the search feature off the tab shows its
 * not-enabled notice and the query is discarded, never carried into a later call.
 */
/**
 * The layout's receiver token, provided around the right panel so the search
 * panel takes requests only for the layout it is mounted in (and only while
 * that layout is the newest one registered for the call).
 */
export const InCallSearchScope = createContext<symbol | null>(null);

export function useInCallSearchTab(callId: string, layout: InCallSearchLayout): symbol {
  const [token] = useState(() => Symbol('in-call-search-layout'));
  const optionsRef = useRef(layout);
  optionsRef.current = layout;
  useEffect(() => {
    if (!callId) return;
    const unregister = registerInCallSearchReceiver(callId, token);
    const listener = (event: Event) => {
      const request = (event as CustomEvent<InCallSearchRequest>).detail;
      if (request?.callId !== callId) return;
      const current = optionsRef.current;
      current.setMainTab('search');
      if (current.rightPanelCollapsed) current.toggleRightPanel();
      if (!current.searchEnabled) takePendingInCallSearch(callId);
    };
    window.addEventListener(IN_CALL_SEARCH_EVENT, listener);
    return () => {
      window.removeEventListener(IN_CALL_SEARCH_EVENT, listener);
      unregister();
    };
  }, [callId, token]);
  return token;
}

/** The header's side: the call id to search in, re-rendering when the call registers or leaves. */
export function useInCallSearchCallId(pathname: string | null | undefined): string | null {
  return useSyncExternalStore(
    subscribeInCallSearchReceivers,
    () => inCallSearchReceiverFor(pathname),
    () => null,
  );
}
